import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
} from "@mui/material";

const vehicleGroupCellStyle = {
  minWidth: 100, // Wider vehicle group column
  width: 100,
  maxWidth: 100,
  fontWeight: 500,
  border: "1px solid #e3e4e4ff",
  textAlign: "center",
  whiteSpace: "nowrap",
  fontSize: "0.75rem",
  padding: "2px 2px",
  verticalAlign: "middle",
};

const otherCellsStyle = {
  minWidth: 40, // Narrower for other columns
  width: 40,
  maxWidth: 40,
  fontWeight: 500,
  border: "1px solid #e3e4e4ff",
  textAlign: "center",
  whiteSpace: "nowrap",
  fontSize: "0.75rem",
  padding: "4px 6px",
  verticalAlign: "middle",
};

// Your final fields arrays

const commonDataFields = [
  { label: "Hours", field: "hours", type: "number" },
  { label: "KMs", field: "kms", type: "number" },
];

const customerDataFields = [
  { label: "SlabRate", field: "sale_slab_rate", type: "number" },
  { label: "ExtraKm", field: "sale_extra_km_rate", type: "number" },
  { label: "ExtraHr", field: "sale_extra_hour_rate", type: "number" },
  { label: "Airport", field: "sale_airport_transfer_rate", type: "number" },
  { label: "OSRate", field: "sale_os_km_rate", type: "number" },
  { label: "Batta", field: "sale_driver_batta", type: "number" },
];

const vehicleVendorDataFields = [
  { label: "SlabRate", field: "purchase_slab_rate", type: "number" },
  { label: "ExtraKm", field: "purchase_extra_km_rate", type: "number" },
  { label: "ExtraHr", field: "purchase_extra_hour_rate", type: "number" },
  { label: "Airport", field: "purchase_airport_transfer_rate", type: "number" },
  { label: "OSRate", field: "purchase_os_km_rate", type: "number" },
  { label: "Batta", field: "purchase_driver_batta", type: "number" },
];

// Combine vehicle group first, then customer then vehicle/vendor
const allDataFields = [
  { label: "Vehicle Group", field: "vehicleGroupId" },
  ...commonDataFields,
  ...customerDataFields,
  ...vehicleVendorDataFields,
];

export default function ExpandableTariffTable({
  allTariffs = [],
  vehicleGroupLabel,
  locationLabel,
  onClose,
}) {
  const tariffsByLocation = allTariffs.reduce((acc, tariff) => {
    const locName = locationLabel
      ? locationLabel(tariff.locationId ?? tariff.categoryid)
      : "Unknown";
    if (!acc[locName]) acc[locName] = [];
    acc[locName].push(tariff);
    return acc;
  }, {});

  return (
    <Box sx={{ maxHeight: 600, p: 1.5 }}>
      <Typography sx={{ mb: 1, fontWeight: "bold", fontSize: "0.8rem" }}>
        All Tariffs: 
      </Typography>

      {Object.entries(tariffsByLocation).map(([location, tariffs]) => (
        <Box
          key={location}
          sx={{
            mb: 1,
            p: 2,
            border: "1px solid #ccc",
            borderRadius: 2,
            backgroundColor: "#fafafa",
          }}
        >
          <Typography sx={{ mb: 1, fontWeight: "bold" }}>
            Location: {location}
          </Typography>

          <Table
            size="small"
            stickyHeader
            sx={{ tableLayout: "fixed", minWidth: 1000 }}
          >
            <TableHead>
              {/* Group headers */}
              <TableRow>
                <TableCell
                  rowSpan={2}
                  sx={{
                    ...vehicleGroupCellStyle,
                    fontSize: "0.75rem",
                    backgroundColor: "#f5f5f5", // or any smaller value you like (0.75rem = 12px approx)
                  }}
                >
                  Vehicle Group
                </TableCell>
                <TableCell
                  colSpan={commonDataFields.length}
                  sx={{
                    textAlign: "center",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #e3e4e4ff",
                    padding: "2px 2px",
                  }}
                >
                  Slab
                </TableCell>

                <TableCell
                  colSpan={customerDataFields.length}
                  sx={{
                    textAlign: "center",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #e3e4e4ff",
                    padding: "2px 2px",
                  }}
                >
                  Customer Group
                </TableCell>
                <TableCell
                  colSpan={vehicleVendorDataFields.length}
                  sx={{
                    textAlign: "center",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #e3e4e4ff",
                    padding: "2px 2px",
                  }}
                >
                  Vehicle/Vendor Group
                </TableCell>
              </TableRow>

              {/* Sub headers */}
              <TableRow>
                {commonDataFields.map(({ label }, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      padding: "2px 4px",
                      minWidth: 40,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #e3e4e4ff",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}

                {customerDataFields.map(({ label }, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      padding: "2px 4px",
                      minWidth: 40,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #e3e4e4ff",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
                {vehicleVendorDataFields.map(({ label }, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      padding: "2px 4px",
                      minWidth: 40,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #e3e4e4ff",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {tariffs.map((row, idx) => (
                <TableRow key={idx}>
                  {allDataFields.map(({ field, type }, jdx) => (
                    <TableCell
                      key={jdx}
                      sx={
                        field === "vehicleGroupId"
                          ? vehicleGroupCellStyle
                          : otherCellsStyle
                      }
                    >
                      {field === "vehicleGroupId"
                        ? vehicleGroupLabel
                          ? vehicleGroupLabel(row.vehicleGroupId)
                          : row.vehicleGroupId
                        : type === "checkbox"
                        ? row[field]
                          ? "✓"
                          : ""
                        : row[field] ?? ""}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}

      <Box sx={{ mt: 2, textAlign: "right" }}>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </Box>
    </Box>
  );
}
